/**
 * Role widoczne w panelu.
 *
 * Lustro `shared/permissions.ts` po stronie API (nazwy i kolejność też).
 * Rozstrzyga zawsze serwer — front tylko nie proponuje tego, co i tak
 * zostanie odrzucone, żeby nie podsuwać operatorowi ślepych zaułków.
 *
 * Role działowe (01.10.2026, wersja do akceptacji): co która widzi, mówi
 * mapa uprawnień — `admin/permissions.ts`. Nazwy jak w dokumentach procesu:
 * „opiekun pojazdów” i „planista główny” (podsumowanie statusu z 29.09).
 */
export const ROLES = [
  { id: 'ADMIN', name: 'Administrator' },
  { id: 'COMPANY_ADMIN', name: 'Administrator spółki' },
  { id: 'FTL_MASTER', name: 'Planista główny FTL (master)' },
  { id: 'PLANNER_FTL', name: 'Planista FTL (opiekun pojazdów)' },
  { id: 'TRADER', name: 'Trader' },
  // Team Leaderzy działów (decyzja użytkownika 24.10.2026) — każdy za swoim
  // działem, jak w API. CS i Administracji tylko z `intakeDepartments`
  // (`INTAKE_ROLE_IDS`), Trade bez przełącznika.
  { id: 'TRADE_LEAD', name: 'Team Leader Trade' },
  // Działy przyjęcia zleceń (makieta back office v2, fala B, 21.10.2026) —
  // na produkcji od 0.31.7, do nadania bez przełącznika.
  { id: 'CS', name: 'CS' },
  { id: 'CS_LEAD', name: 'Team Leader CS' },
  { id: 'ADMINISTRATION', name: 'Administracja' },
  { id: 'ADMINISTRATION_LEAD', name: 'Team Leader Administracji' },
  { id: 'USER', name: 'Użytkownik' },
  { id: 'GUEST', name: 'Gość' },
  { id: 'API_CLIENT', name: 'Konto maszynowe (API)' },
];

/** Role, które administrator spółki może nadać — jak `assignableRoles` w API. */
const COMPANY_ADMIN_ASSIGNABLE = [
  'USER',
  'GUEST',
  'COMPANY_ADMIN',
  'FTL_MASTER',
  'PLANNER_FTL',
  'TRADER',
  'CS',
  'ADMINISTRATION',
  'CS_LEAD',
  'ADMINISTRATION_LEAD',
  'TRADE_LEAD',
];

/**
 * Role nadawane tylko z przełącznikiem `intakeDepartments` — jak
 * `INTAKE_ROLES_BEHIND_FLAG` w API: Team Leaderzy CS i Administracji. Same
 * role CS i Administracja są na produkcji od 0.31.7, więc nadaje się je bez
 * przełącznika (wydanie 26.10.2026: wersja „obecna” = produkcja).
 */
export const INTAKE_ROLE_IDS: readonly string[] = ['CS_LEAD', 'ADMINISTRATION_LEAD'];

/**
 * Role do nadania w tej wersji: bez Team Leaderów CS i Administracji, gdy
 * przełącznik `intakeDepartments` jest wyłączony — API i tak by odmówiło.
 * Rola, którą konto już ma, zostaje na liście (edycja bez zmiany roli).
 */
export function assignableRolesFor(
  granterRole: string | null | undefined,
  intakeDepartmentsOn: boolean,
  currentRole?: string | null,
) {
  return assignableRoles(granterRole).filter(
    (r) => intakeDepartmentsOn || !INTAKE_ROLE_IDS.includes(r.id) || r.id === currentRole,
  );
}

/** Role, które wolno nadawać osobie o danej roli. */
export function assignableRoles(granterRole: string | null | undefined) {
  if (granterRole === 'ADMIN') return ROLES;
  if (granterRole === 'COMPANY_ADMIN') {
    return ROLES.filter((r) => COMPANY_ADMIN_ASSIGNABLE.includes(r.id));
  }
  return [];
}

/**
 * Czytelna nazwa roli; nieznany identyfikator pokazujemy, jak przyszedł.
 * `custom` — role własne z centrum ról (22.10.2026), nazwy z API.
 */
export function roleLabel(
  id: unknown,
  custom: ReadonlyArray<{ id: string; name: string }> = [],
): string {
  const r = ROLES.find((x) => x.id === id) ?? custom.find((x) => x.id === id);
  return r ? r.name : String(id ?? '—');
}

/** Najkrótsze hasło — ta sama wartość co w API. */
export const MIN_PASSWORD_LENGTH = 12;
